
import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from '@/constants/UserContext';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/useToast';
import { useDebounce } from '@/hooks/useDebounce';
import { logger } from '@/utils/logger';
import { NavBar, Toast } from '@/components/ui';
import { Search } from 'lucide-react';
import EventPreviewModal, { type ProfileEvent } from '@/components/common/EventPreviewModal';
import { PullToRefresh } from '@/components/common/PullToRefresh';
import { Box, Flex, Input, Text } from '@chakra-ui/react';
import {
  ProfileHeader,
  ProfileActions,
  ProfileInfo,
  AboutSection,
  SkillsSection,
  CertificatesSection,
  LatestActivitySection,
  CertificateModal,
  ProfileSearchResults,
} from './components';
import { VolunteerActivity } from './components/LatestActivitySection';
import { profileBackTarget } from './profileBackTarget';

interface Certificate {
  id: string;
  title: string;
  image_url: string;
  date: string;
}

/** PostgREST `.or()` splits on commas; strip/guard LIKE wildcards in user input */
function sanitizeProfileSearchQuery(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  return trimmed
    .replace(/,/g, ' ')
    .replace(/%/g, '')
    .replace(/_/g, '')
    .slice(0, 80);
}

type OtherUserProfileRow = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  phone?: string | null;
  profile_image: string | null;
  location?: string | null;
  skills?: string[] | null;
  created_at?: string;
  age?: number | string | null;
  hours?: number | string | null;
  tshirtSize?: string | null;
  description?: string | null;
  date_of_birth?: string | null;
  background_image?: string | null;
  role?: string | null;
};

export function ProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, updateUser } = useUser();
  const { showToast, hideToast, toast } = useToast();
  const [otherUserProfile, setOtherUserProfile] = useState<OtherUserProfileRow | null>(null);
  /** Viewing someone else's profile that we are not allowed to (or failed to) read. */
  const [otherProfileUnavailable, setOtherProfileUnavailable] = useState(false);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [selectedCertificateModal, setSelectedCertificateModal] = useState<Certificate | null>(null);
  const [showEventPreview, setShowEventPreview] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<ProfileEvent | null>(null);
  const [volunteerActivities, setVolunteerActivities] = useState<VolunteerActivity[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [loadedActivities, setLoadedActivities] = useState(5);
  const [isLoadingMoreActivities, setIsLoadingMoreActivities] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string;
    profile_image: string | null;
    role: string;
  }>>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  /** Sum of volunteers.hours across events (profiles.hours is not maintained). */
  const [totalVolunteerHours, setTotalVolunteerHours] = useState<number>(0);

  const profileId = searchParams.get('id') && searchParams.get('id') !== user?.id 
    ? searchParams.get('id')! 
    : user?.id;
  const isOtherUser = searchParams.get('id') && searchParams.get('id') !== user?.id;

  const handleBack = () => {
    // React Router labels the first entry of a session 'default', which is how a profile
    // opened cold from a shared link is told apart from one pushed on top of a page.
    const target = profileBackTarget(
      (location.state as { returnTo?: unknown } | null)?.returnTo,
      location.key !== 'default',
    );
    if (target === -1) navigate(-1);
    else navigate(target, { replace: true });
  };

  const displayProfile = isOtherUser ? otherUserProfile : user;
  const isOrganizationProfile = displayProfile?.role === 'organization';

  const loadMoreActivities = useCallback(() => {
    if (!volunteerActivities || isLoadingMoreActivities) return;
    
    if (loadedActivities < volunteerActivities.length) {
      setIsLoadingMoreActivities(true);
      
      setTimeout(() => {
        setLoadedActivities(prev => Math.min(prev + 5, volunteerActivities.length));
        setIsLoadingMoreActivities(false);
      }, 1500);
    }
  }, [volunteerActivities, loadedActivities, isLoadingMoreActivities]);

  const fetchProfileData = useCallback(async () => {
    if (!profileId) return;
    try {
      if (isOtherUser) {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, email, phone, profile_image, location, skills, created_at, age, hours, tshirtSize, description, date_of_birth, background_image, role')
          .eq('id', profileId)
          .maybeSingle();
        if (error) {
          // Blank profile pages used to be indistinguishable from a permission problem.
          logger.error('ProfilePage: profile read failed', {
            code: error.code,
            message: error.message,
            details: error.details,
            profileId,
          });
        }
        setOtherUserProfile(data ?? null);
        setOtherProfileUnavailable(!data);
      } else if (user?.id) {
        const { data: userProfile } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, email, phone, date_of_birth, profile_image, location, skills, created_at, age, hours, tshirtSize, description, background_image, role')
          .eq('id', user.id)
          .single();
        if (userProfile) {
          if (!userProfile.email && user.email) {
            userProfile.email = user.email;
          }
          if (userProfile.id === user.id) {
            updateUser(userProfile);
          }
        }
      }
    } catch (error) {
      logger.error('Error fetching profile:', error);
    }
    // Not the whole `user`: updateUser replaces it, which would refetch in a loop.
  }, [profileId, isOtherUser, user?.id, user?.email, updateUser]);

  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData]);

  const fetchCertificates = useCallback(async () => {
    if (!profileId) return;
    try {
      const { data } = await supabase
        .from('certificates')
        .select('*')
        .eq('user_id', profileId)
        .eq('is_public', true)
        .order('date', { ascending: false });

      if (data) {
        setCertificates(data);
      }
    } catch (error) {
      logger.error('Error fetching certificates:', error);
    }
  }, [profileId]);

  useEffect(() => {
    fetchCertificates();
  }, [fetchCertificates]);

  const fetchTotalVolunteerHours = useCallback(async () => {
    if (!profileId) {
      setTotalVolunteerHours(0);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('volunteers')
        .select('hours')
        .eq('volunteer_id', profileId);

      if (error || !data) {
        setTotalVolunteerHours(0);
        return;
      }
      const sum = data.reduce((acc, row) => acc + (Number(row.hours) || 0), 0);
      setTotalVolunteerHours(sum);
    } catch {
      setTotalVolunteerHours(0);
    }
  }, [profileId]);

  useEffect(() => {
    fetchTotalVolunteerHours();
  }, [fetchTotalVolunteerHours]);

  const fetchVolunteerActivities = useCallback(async () => {
    try {
      setActivitiesLoading(true);
      
      if (!profileId) {
        setVolunteerActivities([]);
        return;
      }

      const { data: volunteerEvents, error } = await supabase
        .from('volunteers')
        .select(`
          id,
          department,
          hours,
          time_slots,
          created_at,
          event_id,
          events (
            id,
            name,
            location,
            image,
            start_date,
            end_date,
            status,
            is_active
          )
        `)
        .eq('volunteer_id', profileId)
        .order('created_at', { ascending: false });

      if (error) {
        setVolunteerActivities([]);
        return;
      }

      if (!volunteerEvents || volunteerEvents.length === 0) {
        setVolunteerActivities([]);
        return;
      }

      // `events` is a to-one embed; the select-string parser types it as an array.
      type VolunteerEventRow = {
        id: string;
        department: string | null;
        hours: number | null;
        time_slots: string | null;
        created_at: string;
        events: ProfileEvent | null;
      };
      const activities = (volunteerEvents as unknown as VolunteerEventRow[])
        .filter((item): item is VolunteerEventRow & { events: ProfileEvent } => Boolean(item.events))
        .map((item) => {
          const event = item.events;
          
          let dateDisplay = 'Ongoing';
          if (event.start_date && event.end_date) {
            const startDate = new Date(event.start_date);
            const endDate = new Date(event.end_date);
            
            const months: { [key: number]: string } = {
              0: 'IAN', 1: 'FEB', 2: 'MAR', 3: 'APR',
              4: 'MAI', 5: 'IUN', 6: 'IUL', 7: 'AUG',
              8: 'SEP', 9: 'OCT', 10: 'NOV', 11: 'DEC'
            };
            
            const startDay = startDate.getDate();
            const startMonth = months[startDate.getMonth()];
            const endDay = endDate.getDate();
            const endMonth = months[endDate.getMonth()];
            
            if (
              startDate.getDate() === endDate.getDate() &&
              startDate.getMonth() === endDate.getMonth() &&
              startDate.getFullYear() === endDate.getFullYear()
            ) {
              dateDisplay = `${startDay} ${startMonth}`;
            } else {
              dateDisplay = `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
            }
          }
          
          return {
            id: item.id,
            title: event.name,
            location: event.location || 'Location TBD',
            date: dateDisplay,
            image: typeof event.image === 'string' && event.image.trim() !== ''
              ? event.image
              : null,
            department: item.department || 'General',
            hours: item.hours || 0,
            timeSlots: item.time_slots || 'TBD',
            status: event.status || 'active',
            created_at: item.created_at,
            events: event,
          };
        });
      
      setVolunteerActivities(activities);
    } catch (error) {
      setVolunteerActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    if (profileId) {
      fetchVolunteerActivities();
    }
  }, [profileId, fetchVolunteerActivities]);

  useEffect(() => {
    setLoadedActivities(5);
  }, [profileId]);

  const handleEventPreview = async (activity: VolunteerActivity) => {
    try {
      if (activity.events) {
        setSelectedEvent(activity.events);
        setShowEventPreview(true);
        return;
      }

      if (activity.title && activity.location) {
        const { data: eventData, error: eventError } = await supabase
          .from('events')
          .select(`
            id,
            name,
            location,
            image,
            start_date,
            end_date,
            is_active,
            is_public,
            created_at,
            created_by,
            time_intervals,
            departments,
            slots,
            status
          `)
          .eq('name', activity.title)
          .eq('location', activity.location)
          .single();

        if (eventError || !eventData) {
          const fallbackEventData: ProfileEvent = {
            id: activity.id,
            name: activity.title,
            location: activity.location,
            image: activity.image,
            start_date: activity.created_at,
            end_date: undefined,
            is_active: String(activity.status ?? '').toLowerCase() === 'active',
            is_public: true,
            created_at: activity.created_at,
            created_by: undefined,
            time_intervals: activity.timeSlots ? [activity.timeSlots] : [],
            departments: activity.department ? [activity.department] : [],
            slots: undefined,
            status: activity.status,
          };
          setSelectedEvent(fallbackEventData);
          setShowEventPreview(true);
          return;
        }

        const completeEventData: ProfileEvent = {
          ...eventData,
        };

        setSelectedEvent(completeEventData);
        setShowEventPreview(true);
      }
    } catch (error) {
      logger.error('Error loading event details:', error);
    }
  };

  const onRefresh = async () => {
    await Promise.all([
      fetchProfileData(),
      fetchCertificates(),
      fetchVolunteerActivities(),
      fetchTotalVolunteerHours(),
    ]);
  };

  const handleShare = async () => {
    const profileIdToShare = displayProfile?.id;
    const profileLink = `${window.location.origin}/profile${profileIdToShare ? `?id=${profileIdToShare}` : ''}`;
    
    try {
      await navigator.clipboard.writeText(profileLink);
      showToast('Profile link copied! The profile link has been copied to your clipboard.', 'success');
    } catch (error) {
      logger.error('Failed to copy link:', error);
      showToast('Failed to copy profile link. Please try again.', 'error');
    }
  };

  useEffect(() => {
    const safeQuery = sanitizeProfileSearchQuery(debouncedSearchQuery);
    if (!safeQuery) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    let cancelled = false;

    const searchProfiles = async () => {
      setIsSearching(true);
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, email, profile_image, role')
          .or(`first_name.ilike.%${safeQuery}%,last_name.ilike.%${safeQuery}%,email.ilike.%${safeQuery}%`)
          .limit(10);

        if (error) throw error;
        if (!cancelled) {
          setSearchResults(data || []);
        }
      } catch (error) {
        logger.error('Error searching profiles:', error);
        if (!cancelled) {
          setSearchResults([]);
        }
      } finally {
        if (!cancelled) {
          setIsSearching(false);
        }
      }
    };

    searchProfiles();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearchQuery]);

  return (
    <Box
      minH="100vh"
      pb={{ base: 'var(--mobile-bottom-safe)', lg: '6' }}
      bg="white"
      pl={{ base: "0", lg: "var(--sidebar-width)" }}
      transition="padding-left 0.3s cubic-bezier(0.4,0,0.2,1)"
    >
      <Box flex="1" bg="white">
        <PullToRefresh onRefresh={onRefresh}>
        <Box
          flex="1"
          maxW={{ base: '100%', md: '920px', xl: '1040px' }}
          mx="auto"
        >
          <ProfileHeader
            backgroundImage={displayProfile?.background_image}
            profileImage={displayProfile?.profile_image}
            onBack={isOtherUser ? handleBack : undefined}
            searchBar={
              <Box position="relative">
                <Flex
                  position="relative"
                  align="center"
                  bg="rgba(255,255,255,0.9)"
                  backdropFilter="blur(4px)"
                  borderRadius="xl"
                  px="4"
                  minH="12"
                  boxShadow="0 8px 24px rgba(0,0,0,0.12)"
                  transition="box-shadow 0.2s"
                  _focusWithin={{ boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 0 0 2px rgba(255,255,255,0.5)" }}
                >
                  <Box as="span" color="volyx.darkGray" flexShrink={0} mr="3" display="inline-flex">
                    <Search size={20} aria-hidden />
                  </Box>
                  <Input
                    type="search"
                    id="profile-search"
                    name="search"
                    aria-label="Search profiles"
                    flex="1"
                    border="none"
                    bg="transparent"
                    fontSize="md"
                    fontFamily="body"
                    color="volyx.text"
                    outline="none"
                    minW="0"
                    _placeholder={{ color: "volyx.darkGray" }}
                    placeholder="Search profiles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoComplete="off"
                  />
                </Flex>
                
                {debouncedSearchQuery.trim() && (
                  <ProfileSearchResults
                    results={searchResults}
                    isLoading={isSearching}
                    onSelect={() => setSearchQuery('')}
                  />
                )}
              </Box>
            }
          />

          {!isOtherUser ? (
            <ProfileActions
              onEdit={() => navigate('/settings')}
              onShare={handleShare}
            />
          ) : (
            <Box mt="80px" mb="24px" aria-hidden />
          )}

          {isOtherUser && otherProfileUnavailable && (
            <Box px="4" pb="2">
              <Text fontSize="sm" color="volyx.error" m="0">
                This profile could not be loaded. You may not have permission to view it.
              </Text>
            </Box>
          )}

          <ProfileInfo
            firstName={displayProfile?.first_name ?? undefined}
            lastName={displayProfile?.last_name ?? undefined}
            role={displayProfile?.role ?? undefined}
            age={displayProfile?.age ?? undefined}
            hours={totalVolunteerHours}
            tshirtSize={displayProfile?.tshirtSize ?? undefined}
            showStats={!isOrganizationProfile}
          />

          <AboutSection
            description={displayProfile?.description}
          />

          {!isOrganizationProfile && (
            <>
              <SkillsSection
                skills={displayProfile?.skills ?? undefined}
              />

              <CertificatesSection
                certificates={certificates}
                onCertificateClick={setSelectedCertificateModal}
              />
            </>
          )}

          <LatestActivitySection
            activities={volunteerActivities}
            loading={activitiesLoading}
            loadedActivities={loadedActivities}
            isLoadingMore={isLoadingMoreActivities}
            onActivityClick={handleEventPreview}
            onLoadMore={loadMoreActivities}
          />
        </Box>
        </PullToRefresh>

        <CertificateModal
          certificate={selectedCertificateModal}
          onClose={() => setSelectedCertificateModal(null)}
          onError={(message) => showToast(message, 'error')}
        />

        <EventPreviewModal
          visible={showEventPreview}
          onClose={() => setShowEventPreview(false)}
          isOrganization={user?.role === 'organization'}
          hideOrganizationInfo={false}
          event={selectedEvent}
          onJoinEvent={() => {
            setShowEventPreview(false);
          }}
          onOpenVolunteerTasks={(eventId) =>
            navigate(`/volunteer-tasks?event=${encodeURIComponent(eventId)}`)
          }
          onLeftEvent={() => {
            setShowEventPreview(false);
            void onRefresh();
          }}
        />

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={hideToast}
          />
        )}
      </Box>
      <NavBar />
    </Box>
  );
}
